import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Weight, AlertTriangle, TrendingUp, TrendingDown, CheckCircle2 } from "lucide-react";
import FieldRow from "./FieldRow";
import { computeMargin, marginTone } from "@/lib/margin";
import { useMarginPreference } from "@/lib/marginPreference";

export default function SaleRowFields({ row, onChange, products }) {
  const showMargin = useMarginPreference();
  const isWeight = row.sale_mode === "weight" || !!row.weight;
  const linkedProduct = products.find((p) => p.id === row.product_id);

  // Prezzo letto dallo scontrino (vendita)
  const scanPricePerKg = Number(row.price_per_kg) || 0;
  const scanUnitPrice = Number(row.unit_price) || 0;

  // Prezzo attuale di listino del prodotto
  const listingPricePerKg = Number(linkedProduct?.price_per_kg) || 0;
  const listingUnitPrice = Number(linkedProduct?.price) || 0;

  // Confronto: c'è un prezzo nuovo da imparare?
  const hasNewWeightPrice = isWeight && scanPricePerKg > 0;
  const hasNewPiecePrice = !isWeight && scanUnitPrice > 0;
  const isFirstWeightPrice = hasNewWeightPrice && listingPricePerKg === 0;
  const isFirstPiecePrice = hasNewPiecePrice && listingUnitPrice === 0;
  const isWeightPriceDifferent = hasNewWeightPrice && listingPricePerKg > 0 && Math.abs(scanPricePerKg - listingPricePerKg) >= 0.01;
  const isPiecePriceDifferent = hasNewPiecePrice && listingUnitPrice > 0 && Math.abs(scanUnitPrice - listingUnitPrice) >= 0.01;

  // Default: __update_listing_price=true se nuovo, false se diverso (richiede conferma utente)
  const updatePref = row.__update_listing_price;
  const isFirstPrice = isFirstWeightPrice || isFirstPiecePrice;
  const isDifferentPrice = isWeightPriceDifferent || isPiecePriceDifferent;
  const willUpdateListing = updatePref === undefined ? isFirstPrice : !!updatePref;

  // Margine dinamico calcolato sul prezzo dello scontrino e il costo del prodotto
  const marginPreview = (() => {
    if (!linkedProduct) return null;
    const previewProduct = isWeight
      ? { ...linkedProduct, price_per_kg: scanPricePerKg }
      : { ...linkedProduct, price: scanUnitPrice };
    return computeMargin(previewProduct);
  })();
  return (
    <div className="space-y-2.5 pl-0 sm:pl-7 min-w-0">
      <FieldRow label="Prodotto letto dallo scontrino">
        <Input value={row.product_name || ""} disabled className="bg-[#F4F1ED]" />
      </FieldRow>
      <FieldRow
        label={
          <span className="inline-flex items-center gap-2">
            <span>Associa al magazzino *</span>
            {row.__auto_matched && row.product_id && row.product_id !== "__create_new__" && (
              <span
                data-testid={`sale-auto-match-badge-${row.__id}`}
                className="normal-case tracking-normal inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#E8F3EC] text-[#2B5A3F] border border-[#C5E1D1] text-[10px] font-semibold"
                title="Associazione proposta automaticamente dallo scanner"
              >
                <CheckCircle2 size={10} />
                match
              </span>
            )}
          </span>
        }
      >
        <Select
          value={row.product_id || ""}
          onValueChange={(v) => onChange({ product_id: v, __auto_matched: false })}
        >
          <SelectTrigger data-testid={`sale-associate-${row.__id}`}>
            <SelectValue placeholder="Scegli prodotto" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__create_new__" data-testid={`sale-associate-new-${row.__id}`}>
              <span className="font-semibold text-[#2B5A3F]">+ Nuovo prodotto (altro)</span>
            </SelectItem>
            {products.map((p) => {
              const stockLabel = p.stock_kg !== null && p.stock_kg !== undefined
                ? `${p.stock_kg.toFixed(3)} kg`
                : `${p.stock} pz`;
              const lotSegment = p.batch ? ` — Lotto ${p.batch}` : "";
              return (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}{lotSegment} ({stockLabel})
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {row.product_id === "__create_new__" && (
          <div data-testid={`sale-new-product-banner-${row.__id}`} className="mt-2 p-2.5 rounded-md bg-[#E8F3EC] border border-[#C5E1D1] text-xs text-[#2B5A3F] space-y-2">
            <div>
              <strong>Crea nuovo prodotto e registra la vendita.</strong>
              {row.weight ? <> Peso: <span className="font-mono">{row.weight}</span></> : null}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#2B5A3F]/80 font-medium block mb-1">
                Nome del nuovo prodotto (puoi modificarlo)
              </label>
              <Input
                data-testid={`sale-new-product-name-${row.__id}`}
                value={row.__new_product_name ?? row.product_name ?? ""}
                onChange={(e) => onChange({ __new_product_name: e.target.value })}
                placeholder={row.product_name || "Nome prodotto"}
                className="bg-white border-[#C5E1D1] focus:border-[#2B5A3F] text-sm"
              />
              <div className="text-[10px] text-[#5C5F5A] mt-1">
                Verrà aggiunto al magazzino con questo nome. La prossima scansione lo riconoscerà automaticamente.
              </div>
            </div>
          </div>
        )}
      </FieldRow>

      {/* Banner: aggiornamento prezzo di listino */}
      {linkedProduct && isFirstPrice && (
        <div data-testid={`sale-price-firstset-${row.__id}`} className="flex items-start gap-2 p-2.5 rounded-md bg-[#E8F3EC] border border-[#C5E1D1] text-xs">
          <CheckCircle2 size={14} className="text-[#2B5A3F] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#2B5A3F]">Imposto il prezzo di vendita di &quot;{linkedProduct.name}&quot;</div>
            <div className="text-[#2B5A3F]/80 mt-0.5">
              Lo scontrino indica <span className="font-mono font-semibold">{isWeight ? `${scanPricePerKg.toFixed(2)} €/kg` : `${scanUnitPrice.toFixed(2)} €/pz`}</span>. Verrà salvato come prezzo di listino.
            </div>
          </div>
        </div>
      )}

      {linkedProduct && isDifferentPrice && (
        <div data-testid={`sale-price-changed-${row.__id}`} className="rounded-md bg-[#FDF6E3] border border-[#F2DEB0] p-2.5 text-xs space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-[#8B6518] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-[#8B6518]">Prezzo diverso dal listino</div>
              <div className="text-[#8B6518]/90 mt-0.5 font-mono">
                Listino: <strong>{isWeight ? `${listingPricePerKg.toFixed(2)} €/kg` : `${listingUnitPrice.toFixed(2)} €/pz`}</strong>
                {" → Scontrino: "}<strong>{isWeight ? `${scanPricePerKg.toFixed(2)} €/kg` : `${scanUnitPrice.toFixed(2)} €/pz`}</strong>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              data-testid={`sale-price-update-${row.__id}`}
              onClick={() => onChange({ __update_listing_price: true })}
              className={`flex-1 w-full sm:w-auto text-xs px-2 py-1.5 rounded-md font-medium transition-all ${
                willUpdateListing
                  ? "bg-[#2E4A3B] text-white"
                  : "bg-white text-[#5C5F5A] border border-[#EAE7E1] hover:border-[#2E4A3B]"
              }`}
            >
              Aggiorna a {isWeight ? `${scanPricePerKg.toFixed(2)} €/kg` : `${scanUnitPrice.toFixed(2)} €/pz`}
            </button>
            <button
              type="button"
              data-testid={`sale-price-keep-${row.__id}`}
              onClick={() => onChange({ __update_listing_price: false })}
              className={`flex-1 w-full sm:w-auto text-xs px-2 py-1.5 rounded-md font-medium transition-all ${
                !willUpdateListing
                  ? "bg-[#2E4A3B] text-white"
                  : "bg-white text-[#5C5F5A] border border-[#EAE7E1] hover:border-[#2E4A3B]"
              }`}
            >
              Tieni {isWeight ? `${listingPricePerKg.toFixed(2)} €/kg` : `${listingUnitPrice.toFixed(2)} €/pz`}
            </button>
          </div>
        </div>
      )}

      {/* Pill margine reale sulla vendita */}
      {marginPreview !== null && showMargin && (
        <div data-testid={`sale-margin-${row.__id}`} className="flex items-center justify-between p-2 rounded-md bg-[#F4F1ED] border border-[#EAE7E1] text-xs">
          <span className="text-[#5C5F5A] font-medium">Margine vendita</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border font-semibold font-mono ${marginTone(marginPreview.pct).cls}`}>
            {marginPreview.pct >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {marginPreview.pct >= 0 ? "+" : ""}{marginPreview.pct}% · €{marginPreview.absolute.toFixed(2)}{isWeight ? "/kg" : "/pz"}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 p-2 rounded-md bg-[#F4F1ED] border border-[#EAE7E1]">
        <Weight size={14} className="text-[#2E4A3B] flex-shrink-0" />
        <span className="text-xs font-medium min-w-0 flex-1 break-words">Vendita a:</span>
        <div className="flex gap-1 flex-shrink-0">
          <button
            type="button"
            data-testid={`sale-mode-weight-${row.__id}`}
            onClick={() => onChange({ sale_mode: "weight" })}
            className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${
              isWeight
                ? "bg-[#2E4A3B] text-white"
                : "bg-white text-[#5C5F5A] border border-[#EAE7E1] hover:border-[#2E4A3B]"
            }`}
          >
            Peso (kg)
          </button>
          <button
            type="button"
            data-testid={`sale-mode-piece-${row.__id}`}
            onClick={() => onChange({ sale_mode: "piece", weight: null, price_per_kg: null })}
            className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${
              !isWeight
                ? "bg-[#2E4A3B] text-white"
                : "bg-white text-[#5C5F5A] border border-[#EAE7E1] hover:border-[#2E4A3B]"
            }`}
          >
            Pezzo
          </button>
        </div>
      </div>

      {isWeight ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <FieldRow label="Peso venduto *">
              <Input
                placeholder="0.250 kg"
                value={row.weight || ""}
                onChange={(e) => onChange({ weight: e.target.value })}
                data-testid={`sale-weight-${row.__id}`}
              />
            </FieldRow>
            <FieldRow label="€ al kg *">
              <Input
                type="number"
                step="0.01"
                value={row.price_per_kg ?? 0}
                onChange={(e) => onChange({ price_per_kg: e.target.value })}
                data-testid={`sale-price-per-kg-${row.__id}`}
                className="font-mono font-semibold"
              />
            </FieldRow>
          </div>
          <FieldRow label="Totale riga (€)">
            <Input
              type="number"
              step="0.01"
              value={row.total_price ?? row.unit_price ?? 0}
              onChange={(e) => onChange({ total_price: e.target.value })}
              className="font-mono"
            />
          </FieldRow>
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <FieldRow label="Quantità (pezzi)">
            <Input
              type="number"
              min="1"
              value={row.quantity || 1}
              onChange={(e) => onChange({ quantity: e.target.value })}
            />
          </FieldRow>
          <FieldRow label="Prezzo unitario (€)">
            <Input
              type="number"
              step="0.01"
              value={row.unit_price ?? 0}
              onChange={(e) => onChange({ unit_price: e.target.value })}
            />
          </FieldRow>
        </div>
      )}

      <FieldRow label="Lotto (opzionale)">
        <Input
          placeholder="Lotto dalla vendita"
          value={row.batch || ""}
          onChange={(e) => onChange({ batch: e.target.value })}
          className="font-mono text-xs"
        />
      </FieldRow>

      {row.total_price && (
        <div className="text-xs text-[#8E918C] text-right">
          Totale letto: € {Number(row.total_price).toFixed(2)}
        </div>
      )}
    </div>
  );
}
